import { useMemo, useState } from "react";
import {
  IncidentTableData,
  IncidentTableFilterState,
  IncidentTableFilterUpdateValue,
  UseIncidentTableFiltersParams,
} from "../../types";
import { bufferOptions } from "../../../../../form/components/risk-monitoring/InputOptions";
import { normaliseTimestamp } from "../../../../../formatting/date-time-manipulation";
import { getDisplayValue } from "../../../../../formatting/string-manipulation";

export const initialIncidentTableFilters: IncidentTableFilterState = {
  dateField: "scannedAt",
  dateFrom: null,
  dateTo: null,
  type: "",
  boundaryBufferKm: "",
  areaOperator: "greaterThan",
  areaM2: "",
};

const includesFilter = (value: string, filter: string): boolean => {
  return value.toLowerCase().includes(filter.trim().toLowerCase());
};

const matchesOptionalFilter = (value: string, filter: string): boolean => {
  return !filter || value === filter;
};

const matchesDateRange = (
  value: number,
  dateFrom: number | null,
  dateTo: number | null,
): boolean => {
  const timestamp = normaliseTimestamp(value);

  if (!timestamp) return false;
  if (dateFrom && timestamp < dateFrom) return false;
  if (dateTo && timestamp > dateTo) return false;

  return true;
};

const matchesAreaFilter = (
  value: number,
  operator: IncidentTableFilterState["areaOperator"],
  filter: string,
): boolean => {
  if (!filter.trim()) return true;

  const numericFilter = Number(filter);

  if (!Number.isFinite(numericFilter))
    return includesFilter(getDisplayValue(value), filter);

  if (operator === "fewerThan") return value < numericFilter;
  if (operator === "equalTo") return value === numericFilter;

  return value > numericFilter;
};

const filterIncidentRows = (
  rows: Array<IncidentTableData>,
  filters: IncidentTableFilterState,
): Array<IncidentTableData> => {
  return rows.filter((row) => {
    return (
      matchesDateRange(
        row[filters.dateField],
        filters.dateFrom,
        filters.dateTo,
      ) &&
      matchesOptionalFilter(getDisplayValue(row.type), filters.type) &&
      matchesOptionalFilter(
        getDisplayValue(row.boundaryBufferKm),
        filters.boundaryBufferKm,
      ) &&
      matchesAreaFilter(row.areaM2, filters.areaOperator, filters.areaM2)
    );
  });
};

export function useIncidentTableFilters({
  rows,
  setPage,
}: UseIncidentTableFiltersParams) {
  const [filters, setFilters] = useState<IncidentTableFilterState>(
    initialIncidentTableFilters,
  );

  const filteredRows = useMemo(() => {
    return filterIncidentRows(rows, filters);
  }, [rows, filters]);

  const updateFilter = <FilterKey extends keyof IncidentTableFilterState>(
    key: FilterKey,
    value: IncidentTableFilterUpdateValue<FilterKey>,
  ): void => {
    setPage(0);
    setFilters((currentFilters) => ({ ...currentFilters, [key]: value }));
  };

  const resetFilters = (): void => {
    setPage(0);
    setFilters(initialIncidentTableFilters);
  };

  return {
    filters,
    filteredRows,
    boundaryFilterOptions: bufferOptions,
    updateFilter,
    resetFilters,
  };
}
